import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, DataToolbar, EmptyState } from '../../../shared-components/ui';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import {
  FundRequest,
  createFundRequestDebit,
  getFundRequests,
  updateFundRequestStatus,
} from '../../../services/request/FundRequests';

const STATUSES = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_REVIEW', label: 'In review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DISBURSED', label: 'Disbursed' },
  { value: 'DECLINED', label: 'Declined' },
];

function rowsFrom(response: any): FundRequest[] {
  const payload = response?.data ?? response;
  return payload?.result ?? [];
}

const FundRequestsManagement: React.FC = () => {
  const [rows, setRows] = useState<FundRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<FundRequest | null>(null);
  const [status, setStatus] = useState('IN_REVIEW');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [debitTarget, setDebitTarget] = useState<FundRequest | null>(null);
  const [debitForm, setDebitForm] = useState({
    amount: '',
    outstanding: '',
    interestRate: '',
    type: 'INTEREST',
    durationType: 'Months',
    startDate: new Date().toISOString().substring(0, 10),
    endDate: '',
    emiAmount: '',
    paymentDate: '5',
    status: 'ACTIVE',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(rowsFrom(await getFundRequests(statusFilter || undefined)));
    } catch (err) {
      console.error(err);
      setError('Failed to load fund requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (row: FundRequest) => {
    setSelected(row);
    setStatus(row.status);
    setNote(row.adminNote ?? '');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateFundRequestStatus(selected.id, status, note);
      toast.success('Fund request updated');
      setSelected(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update fund request');
    } finally {
      setSaving(false);
    }
  };

  const openDebit = (row: FundRequest) => {
    setDebitTarget(row);
    setDebitForm({
      amount: String(row.requestedAmount),
      outstanding: String(row.requestedAmount),
      interestRate: String(row.monthlyInterestRate),
      type: 'INTEREST',
      durationType: 'Months',
      startDate: new Date().toISOString().substring(0, 10),
      endDate: '',
      emiAmount: '',
      paymentDate: '5',
      status: 'ACTIVE',
    });
  };

  const saveDebit = async () => {
    if (!debitTarget) return;
    const amount = Number(debitForm.amount);
    const outstanding = Number(debitForm.outstanding);
    const interestRate = Number(debitForm.interestRate);
    const paymentDate = Number(debitForm.paymentDate);
    const emiAmount = debitForm.emiAmount.trim() ? Number(debitForm.emiAmount) : 0;
    if (!Number.isInteger(amount) || amount < debitTarget.minimumAmount || amount > debitTarget.requestedAmount) {
      toast.error('Amount must stay between the minimum and the requested amount');
      return;
    }
    if (!Number.isInteger(outstanding) || outstanding < 1 || outstanding > amount) {
      toast.error('Outstanding cannot be higher than the amount');
      return;
    }
    if (!Number.isFinite(interestRate) || interestRate <= 0) {
      toast.error('Enter the monthly interest rate');
      return;
    }
    if (!Number.isInteger(paymentDate) || paymentDate < 1 || paymentDate > 31) {
      toast.error('Payment day must be between 1 and 31');
      return;
    }
    setSaving(true);
    try {
      const response = await createFundRequestDebit(debitTarget.id, {
        amount,
        outstanding,
        interestRate,
        type: debitForm.type,
        durationType: debitForm.durationType,
        startDate: debitForm.startDate,
        endDate: debitForm.endDate || undefined,
        emiAmount,
        paymentDate,
        status: debitForm.status,
      });
      toast.success(response?.message || 'Debit created');
      setDebitTarget(null);
      load();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to create debit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Ops"
        title="Fund requests"
        subtitle="Customers ask for an amount, a minimum they can accept, and a monthly interest rate they can manage. After approval, open the debit here. That emails the customer and adds a notification in the app."
        actions={
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Card sx={{ mb: 3, p: 2 }}>
        <DataToolbar>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DataToolbar>
      </Card>
      {rows.length === 0 && !loading ? (
        <EmptyState title="No fund requests" description="Requests from the customer app will appear here." />
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Exact amount</TableCell>
                <TableCell>Minimum</TableCell>
                <TableCell>Monthly rate</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2">{row.customerName || 'Customer'}</Typography>
                    <Typography variant="body2" color="text.secondary">{row.customerEmail}</Typography>
                  </TableCell>
                  <TableCell>{formatCurrency(row.requestedAmount, 'INR', true)}</TableCell>
                  <TableCell>{formatCurrency(row.minimumAmount, 'INR', true)}</TableCell>
                  <TableCell>{row.monthlyInterestRate}% / month</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{row.purpose}</TableCell>
                  <TableCell><Chip size="small" label={row.status.replace('_', ' ')} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {row.status === 'APPROVED' && !row.debitId ? (
                        <Button size="small" variant="contained" onClick={() => openDebit(row)}>Create debit</Button>
                      ) : null}
                      <Button size="small" onClick={() => open(row)}>Review</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Review fund request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Approve the request first. Create debit then opens the facility, emails the customer, and adds an app notification.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                {STATUSES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!debitTarget} onClose={() => setDebitTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Create debit</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Amount must stay between {debitTarget ? formatCurrency(debitTarget.minimumAmount, 'INR', true) : ''} and {debitTarget ? formatCurrency(debitTarget.requestedAmount, 'INR', true) : ''}. This uses the same debit record as the customer page, then emails the customer and posts a notification in the app.
            </Typography>
            <TextField label="Amount" value={debitForm.amount} onChange={(event) => setDebitForm({ ...debitForm, amount: event.target.value, outstanding: event.target.value })} />
            <TextField label="Outstanding" value={debitForm.outstanding} onChange={(event) => setDebitForm({ ...debitForm, outstanding: event.target.value })} />
            <TextField label="Monthly interest rate (%)" value={debitForm.interestRate} onChange={(event) => setDebitForm({ ...debitForm, interestRate: event.target.value })} />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={debitForm.type} onChange={(event) => setDebitForm({ ...debitForm, type: event.target.value })}>
                <MenuItem value="INTEREST">Interest</MenuItem>
                <MenuItem value="SHORT_TERM_DEBIT">Short-term debit</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select label="Duration" value={debitForm.durationType} onChange={(event) => setDebitForm({ ...debitForm, durationType: event.target.value })}>
                {['Days', 'Weeks', 'Months', 'Years'].map((item) => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Start date" type="date" value={debitForm.startDate} onChange={(event) => setDebitForm({ ...debitForm, startDate: event.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="End date" type="date" value={debitForm.endDate} onChange={(event) => setDebitForm({ ...debitForm, endDate: event.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="Instalment on file" value={debitForm.emiAmount} onChange={(event) => setDebitForm({ ...debitForm, emiAmount: event.target.value })} />
            <TextField label="Payment day of month" value={debitForm.paymentDate} onChange={(event) => setDebitForm({ ...debitForm, paymentDate: event.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDebitTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveDebit} disabled={saving}>Create debit</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundRequestsManagement;
